import React, {useState} from 'react';
import {
    Card,
    CardContent,
    CardActions,
    Typography,
    Box,
    Button,
    IconButton,
    Chip,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    CircularProgress,
} from '@mui/material';
import {
    Analytics as AnalyticsIcon,
    MoreVert as MoreIcon,
    Topic as TopicIcon,
    Groups as GroupsIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    School as SchoolIcon,
    Visibility as PublishIcon,
    VisibilityOff as UnpublishIcon,
    ContentCopy as CloneIcon,
} from '@mui/icons-material';

// Helper-Funktion für Academic Level Labels
const getAcademicLevelLabel = (level) => {
    const labels = {
        'bachelor_1': 'Bachelor 1. Sem',
        'bachelor_2': 'Bachelor 2. Sem',
        'bachelor_3': 'Bachelor 3. Sem',
        'bachelor_4': 'Bachelor 4. Sem',
        'bachelor_5': 'Bachelor 5. Sem',
        'bachelor_6': 'Bachelor 6. Sem',
        'master_1': 'Master 1. Sem',
        'master_2': 'Master 2. Sem',
        'master_3': 'Master 3. Sem',
        'master_4': 'Master 4. Sem'
    };
    return labels[level] || level;
};

const CourseCard = ({course, onCourseDeleted, onCourseEdit, apiClient}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [confirmationText, setConfirmationText] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [toggling, setToggling] = useState(false);
    const [isPublished, setIsPublished] = useState(course.published || false);

    // Clone state
    const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
    const [cloneName, setCloneName] = useState('');
    const [cloning, setCloning] = useState(false);
    const [cloneError, setCloneError] = useState(null);

    const menuOpen = Boolean(anchorEl);

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleEditClick = () => {
        if (onCourseEdit) {
            onCourseEdit(course);
        }
        handleMenuClose();
    };

    const handleDeleteClick = () => {
        setDeleteDialogOpen(true);
        handleMenuClose();
    };

    const handleDeleteDialogClose = () => {
        if (!deleting) {
            setDeleteDialogOpen(false);
            setConfirmationText('');
            setDeleteError(null);
        }
    };

    const handleDeleteCourse = async () => {
        if (confirmationText !== course.title) {
            setDeleteError('Course name does not match. Please type the exact course title.');
            return;
        }

        setDeleting(true);
        setDeleteError(null);

        try {
            await apiClient.delete(`/api/courses/${course.id}`);
            onCourseDeleted(course.id);
            setDeleteDialogOpen(false);
            setConfirmationText('');
        } catch (error) {
            console.error('Error deleting course:', error);
            setDeleteError(error.message || 'Failed to delete course');
        } finally {
            setDeleting(false);
        }
    };

    const handleTogglePublish = async () => {
        setToggling(true);
        handleMenuClose();

        try {
            await apiClient.put(`/api/courses/${course.id}`, {
                published: !isPublished
            });
            setIsPublished(!isPublished);
        } catch (error) {
            console.error('Error toggling course publish status:', error);
            // Optionally show an error message to the user
        } finally {
            setToggling(false);
        }
    };

    const handleCloneClick = () => {
        setCloneName(`${course.title} (Copy)`);
        setCloneDialogOpen(true);
        handleMenuClose();
    };

    const handleCloneDialogClose = () => {
        if (!cloning) {
            setCloneDialogOpen(false);
            setCloneName('');
            setCloneError(null);
        }
    };

    const handleCloneCourse = async () => {
        if (!cloneName.trim()) {
            setCloneError('Please enter a name for the cloned course');
            return;
        }

        setCloning(true);
        setCloneError(null);

        try {
            const response = await apiClient.post(`/api/courses/${course.id}/clone`, {
                new_course_name: cloneName.trim()
            });

            // Close dialog and reset state
            setCloneDialogOpen(false);
            setCloneName('');

            // Navigate to the newly cloned course
            window.location.href = `/teacher/course/${response.id}`;
        } catch (error) {
            console.error('Error cloning course:', error);
            setCloneError(error.response?.data?.detail || error.message || 'Failed to clone course');
        } finally {
            setCloning(false);
        }
    };

    return (
        <Card sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
            <CardContent sx={{flexGrow: 1}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2}}>
                    <Typography variant="h6" component="h3" gutterBottom>
                        {course.title}
                    </Typography>
                    <IconButton size="small" onClick={handleMenuClick}>
                        <MoreIcon/>
                    </IconButton>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{mb: 2, minHeight: '40px'}}>
                    {course.description || 'No description available'}
                </Typography>

                {/* NEU: Academic Level anzeigen */}
                <Box sx={{mb: 2, display: 'flex', gap: 1}}>
                    {course.academic_level && (
                        <Chip
                            icon={<SchoolIcon/>}
                            label={getAcademicLevelLabel(course.academic_level)}
                            size="small"
                            variant="outlined"
                            sx={{
                                px: 1.5,
                                py: 0.5,
                                '& .MuiChip-icon': {
                                    marginLeft: '8px',
                                    marginRight: '4px'
                                },
                                '& .MuiChip-label': {
                                    paddingRight: '8px'
                                }
                            }}
                        />
                    )}
                    <Chip
                        icon={isPublished ? <PublishIcon/> : <UnpublishIcon/>}
                        label={isPublished ? 'Published' : 'Unpublished'}
                        size="small"
                        color={isPublished ? 'success' : 'default'}
                        variant="outlined"
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            '& .MuiChip-icon': {
                                marginLeft: '8px',
                                marginRight: '4px'
                            },
                            '& .MuiChip-label': {
                                paddingRight: '8px'
                            }
                        }}
                    />
                </Box>

                <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
                    <Chip
                        icon={<GroupsIcon/>}
                        label={`${course.student_count} students`}
                        size="small"
                        variant="outlined"
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            '& .MuiChip-icon': {
                                marginLeft: '8px',
                                marginRight: '4px'
                            },
                            '& .MuiChip-label': {
                                paddingRight: '8px'
                            }
                        }}
                    />
                    <Chip
                        icon={<TopicIcon/>}
                        label={`${course.topic_count} topics`}
                        size="small"
                        variant="outlined"
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            '& .MuiChip-icon': {
                                marginLeft: '8px',
                                marginRight: '4px'
                            },
                            '& .MuiChip-label': {
                                paddingRight: '8px'
                            }
                        }}
                    />
                </Box>
            </CardContent>

            <CardActions sx={{justifyContent: 'space-between', px: 2, pb: 2}}>
                <Button
                    size="small"
                    variant="contained"
                    href={`/teacher/course/${course.id}`}
                    fullWidth
                    sx={{mr: 1}}
                >
                    View Course
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AnalyticsIcon/>}
                    href={`/teacher/analytics/course/${course.id}`}
                    fullWidth
                >
                    Analytics
                </Button>
            </CardActions>

            {/* Menu */}
            <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                transformOrigin={{horizontal: 'right', vertical: 'top'}}
                anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
            >
                <MenuItem onClick={handleTogglePublish} disabled={toggling}>
                    <ListItemIcon>
                        {isPublished ? <UnpublishIcon fontSize="small"/> : <PublishIcon fontSize="small"/>}
                    </ListItemIcon>
                    <ListItemText>{isPublished ? 'Unpublish Course' : 'Publish Course'}</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleEditClick}>
                    <ListItemIcon>
                        <EditIcon fontSize="small"/>
                    </ListItemIcon>
                    <ListItemText>Edit Course</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleCloneClick}>
                    <ListItemIcon>
                        <CloneIcon fontSize="small"/>
                    </ListItemIcon>
                    <ListItemText>Clone Course</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={handleDeleteClick}
                    sx={{color: 'error.main'}}
                >
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" sx={{color: 'error.main'}}/>
                    </ListItemIcon>
                    <ListItemText>Delete Course</ListItemText>
                </MenuItem>
            </Menu>

            {/* Clone Course Dialog */}
            <Dialog
                open={cloneDialogOpen}
                onClose={handleCloneDialogClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Clone Course: {course.title}
                </DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{mb: 3}}>
                        <Typography variant="body2" sx={{fontWeight: 'bold', mb: 1}}>
                            Creating a copy of this course
                        </Typography>
                        <Typography variant="body2">
                            The cloned course will include:
                        </Typography>
                        <Box component="ul" sx={{mt: 1, mb: 0, pl: 2}}>
                            <li>All course topics</li>
                            <li>All forms, sections, questions, and answer options</li>
                            <li>Course settings and academic level</li>
                        </Box>
                        <Typography variant="body2" sx={{mt: 1, fontStyle: 'italic'}}>
                            Note: Student enrollments and course materials will NOT be copied. The cloned course will start as unpublished.
                        </Typography>
                    </Alert>

                    {cloneError && (
                        <Alert severity="error" sx={{mb: 2}}>
                            {cloneError}
                        </Alert>
                    )}

                    <TextField
                        fullWidth
                        label="New Course Name"
                        value={cloneName}
                        onChange={(e) => setCloneName(e.target.value)}
                        disabled={cloning}
                        variant="outlined"
                        autoFocus
                        helperText="Enter a name for the cloned course"
                        sx={{mt: 2}}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloneDialogClose} disabled={cloning}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCloneCourse}
                        color="primary"
                        variant="contained"
                        disabled={cloning || !cloneName.trim()}
                        startIcon={cloning ? <CircularProgress size={20}/> : <CloneIcon/>}
                    >
                        {cloning ? 'Cloning...' : 'Clone Course'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteDialogClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{color: 'error.main'}}>
                    Delete Course: {course.title}
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{mb: 3}}>
                        <Typography variant="body2" sx={{fontWeight: 'bold', mb: 1}}>
                            This action cannot be undone!
                        </Typography>
                        <Typography variant="body2">
                            Deleting this course will:
                        </Typography>
                        <Box component="ul" sx={{mt: 1, mb: 0, pl: 2}}>
                            <li>Remove all course content and materials</li>
                            <li>Remove student access to the course</li>
                            <li>Delete all student progress and answers</li>
                            <li>Remove all analytics data for this course</li>
                        </Box>
                    </Alert>

                    {deleteError && (
                        <Alert severity="error" sx={{mb: 2}}>
                            {deleteError}
                        </Alert>
                    )}

                    <Typography variant="body1" sx={{mb: 2}}>
                        To confirm deletion, please type the course title exactly as shown:
                    </Typography>

                    <Typography variant="body2" sx={{
                        mb: 2,
                        fontWeight: 'bold',
                        fontFamily: 'monospace',
                        backgroundColor: 'grey.100',
                        p: 1,
                        borderRadius: 1
                    }}>
                        {course.title}
                    </Typography>

                    <TextField
                        fullWidth
                        label="Type course title to confirm"
                        value={confirmationText}
                        onChange={(e) => setConfirmationText(e.target.value)}
                        disabled={deleting}
                        variant="outlined"
                        error={deleteError && confirmationText !== course.title}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteDialogClose} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteCourse}
                        color="error"
                        variant="contained"
                        disabled={deleting || confirmationText !== course.title}
                        startIcon={deleting ? <CircularProgress size={20}/> : <DeleteIcon/>}
                    >
                        {deleting ? 'Deleting...' : 'Delete Course'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};

export default CourseCard;